import { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BoomFinanceVerificationSettings,
  VerificationNames
} from "shared/types";
import { Loader } from "shared/components/Loader";
import { Applicant } from "types/application";
import { VerificationReport } from "types/landlord/reports";
import api from "api/landlord/applications";

import { getVerificationQueryKeys } from "../../utils/getVerificationQueryKeys";
import { UnavailableReportView } from "../../components/ReportsFallback/UnavailableReportView";
import { PurchaseApplicationReport } from "./types";
import { PurchaseApplicationReportBody } from "./PurchaseApplicationReportBody";
import { DeclinedFinancingView } from "./components/DeclinedFinancingView";

interface PurchaseApplicationSliceProps {
  applicant: Applicant;
  verificationId: number;
  settings?: BoomFinanceVerificationSettings;
}

export const PurchaseApplicationSlice: FC<PurchaseApplicationSliceProps> = ({
  applicant,
  verificationId,
  settings
}) => {
  const { data, isLoading } = useQuery({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.BoomFinanceVerification,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then(
          (d: {
            verification: VerificationReport<PurchaseApplicationReport>;
          }) => d.verification
        )
  });

  if (isLoading) return <Loader />;

  // PLT-728: the applicant opted out on the lender step. The verification is
  // finished without a report, so show the choice instead of "not available".
  if (data?.declined_financing)
    return <DeclinedFinancingView declinedAt={data.declined_financing_at} />;

  const report = data?.report;
  if (!report) return <UnavailableReportView />;

  return <PurchaseApplicationReportBody report={report} settings={settings} />;
};
